import React, { useEffect, useState } from 'react';
import { FaPlus, FaUpload, FaPen, FaTrash, FaSearch, FaTimes, FaClipboardList } from 'react-icons/fa';
import LoadingState from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { listTemplates, deleteTemplateApi, listCampaignsApi } from '../api/api';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ConfirmationModal from '../components/ConfirmationModal';

const TemplateList = ({ setAlert }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  // templateName -> [campaign names using it], derived from the campaigns
  // list. The templates endpoint alone doesn't say where a scorecard is
  // used, and that's the first thing you want to know before touching one.
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [editTarget, setEditTarget] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleEdit = (name) => setEditTarget(name);
  const confirmEdit = () => {
    navigate(`/templates/${encodeURIComponent(editTarget)}`);
    setEditTarget('');
  };

  const toggleRowExpansion = (name) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        // Usage data is a nicety — if the campaigns call fails the list
        // still renders, just without the "Used in" chips.
        const [tplRes, campRes] = await Promise.all([
          listTemplates(),
          listCampaignsApi().catch(() => null),
        ]);
        setTemplates(tplRes.templates || []);
        if (campRes?.campaigns) {
          const map = {};
          campRes.campaigns.forEach((c) => {
            (c.templates || []).forEach((t) => {
              const key = typeof t === 'object' ? t.templateName || t.S : t;
              if (!key) return;
              if (!map[key]) map[key] = [];
              map[key].push(c.campaignName);
            });
          });
          setUsage(map);
        }
      } catch (err) {
        console.error('Failed to load scorecards:', err);
        setAlert?.({ variant: 'error', heading: 'Error', text: 'Failed to load scorecards.' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [setAlert]);

  const handleDelete = (name) => {
    setDeleteInfo(name);
  };

  const confirmDelete = async () => {
    const name = deleteInfo;
    try {
      const res = await deleteTemplateApi(name);
      setTemplates((t) => t.filter((tmp) => tmp.templateName !== name));

      const campaignsMsg =
        res.removedFromCampaigns && res.removedFromCampaigns.length > 0
          ? ` Removed from campaigns: ${res.removedFromCampaigns.join(', ')}.`
          : '';

      setAlert?.({
        variant: 'success',
        heading: 'Template Deleted',
        text: `${res.message || 'Template deleted successfully.'}${campaignsMsg}`,
      });
    } catch (err) {
      console.error('Delete failed', err);
      const msg = err?.message || 'Failed to delete template.';
      setAlert?.({ variant: 'error', heading: 'Error', text: msg });
    } finally {
      setDeleteInfo(null);
    }
  };

  const renderUsage = (name) => {
    const campaigns = usage[name] || [];
    if (campaigns.length === 0) {
      return <span className="text-xs text-gray-400">Not assigned</span>;
    }
    const isExpanded = expandedRows.has(name);
    const visible = isExpanded ? campaigns : campaigns.slice(0, 2);
    const remaining = campaigns.length - visible.length;
    return (
      <div className="flex items-center flex-wrap gap-1.5">
        {visible.map((c) => (
          <span
            key={c}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-cloudza-50 text-cloudza-700 border border-cloudza-100 text-xs font-medium"
          >
            {c}
          </span>
        ))}
        {remaining > 0 && (
          <button
            onClick={() => toggleRowExpansion(name)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium transition-colors"
            title={campaigns.slice(2).join(', ')}
          >
            +{remaining}
          </button>
        )}
        {isExpanded && campaigns.length > 2 && (
          <button
            onClick={() => toggleRowExpansion(name)}
            className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium transition-colors"
          >
            less
          </button>
        )}
      </div>
    );
  };

  const filtered = templates.filter((t) =>
    t.templateName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Scorecards</h1>
          <p className="text-gray-500 text-sm mt-1">Manage QA scorecards</p>
        </div>
        <div className="flex gap-2" data-tour="scorecard-actions">
          <button onClick={() => navigate('/templates/upload')} className="btn-outline">
            <FaUpload />
            Upload Scorecard
          </button>
          <button onClick={() => navigate('/templates/new')} className="btn-primary">
            <FaPlus />
            New Scorecard
          </button>
        </div>
      </div>
      <div className="card !p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:max-w-xs group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[13px] pointer-events-none transition-colors group-focus-within:text-cloudza-600" />
            <input
              type="text"
              className="input-field !rounded-full !pl-10 !pr-9 !py-2.5"
              placeholder="Search scorecards…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search scorecards"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="text-[11px]" />
              </button>
            )}
          </div>
          {!loading && templates.length > 0 && (
            <span className="text-xs text-gray-400 pr-1">
              {search ? `${filtered.length} of ${templates.length}` : `${templates.length} scorecard${templates.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
        {loading ? (
          <LoadingState variant="rows" header={false} label="Loading scorecards" />
        ) : templates.length === 0 ? (
          <div className="py-14 px-6 text-center">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-coral-50 text-coral-700 flex items-center justify-center">
              <FaClipboardList className="text-lg" />
            </span>
            <p className="mt-4 text-sm font-semibold text-gray-800">No scorecards yet</p>
            <p className="mt-1 text-sm text-gray-400">Create one from scratch, or extract it from an existing document.</p>
            <button onClick={() => navigate('/templates/new')} className="btn-primary mt-5">
              <FaPlus />
              New Scorecard
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 px-6 text-center">
            <p className="text-sm text-gray-400">No scorecards match <span className="font-semibold text-gray-600">“{search}”</span></p>
            <button onClick={() => setSearch('')} className="btn-outline !py-1.5 !px-4 !text-xs mt-4">
              <FaTimes />
              Clear search
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Scorecard</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Used In</th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filtered.map(t => (
                  <tr key={t.templateName} className="hover:bg-cloudza-50/40 transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-coral-400 to-coral-600 text-white text-xs font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
                          {t.templateName.trim().charAt(0)}
                        </span>
                        <span className="font-medium text-gray-900">{t.templateName}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">{renderUsage(t.templateName)}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(t.templateName)}
                          className="btn-outline !py-1 !px-3 !text-xs"
                        >
                          <FaPen />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(t.templateName)}
                          className="btn-outline !py-1 !px-3 !text-xs !text-red-600 !border-red-200 hover:!bg-red-50"
                        >
                          <FaTrash />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmationModal
        open={!!editTarget}
        title="Edit Confirmation"
        message={`Are you sure you want to enter edit mode for ${editTarget}?`}
        confirmText="Continue"
        onClose={() => setEditTarget('')}
        onConfirm={confirmEdit}
      />
      <DeleteConfirmationModal
        open={!!deleteInfo}
        itemName={deleteInfo || ''}
        onClose={() => setDeleteInfo(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default TemplateList;
